import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { AlertCircleIcon, FileWarningIcon, LoaderCircleIcon, SaveIcon, Trash2Icon, UserRoundCheckIcon } from 'lucide-react';
import type { AdminProfile } from '../../hooks/useAdminAccess';
import { type CrmRecord, value } from '../../lib/crm';
import { createEmptyProspectForm, type ProspectFormValues } from '../../lib/prospects/schema';
import type { ProspectIdentityValues } from '../../lib/prospectDuplicateCheck';
import { ProspectService } from '../../lib/supabase/services/prospects.service';
import { ProspectDuplicateNotice } from './ProspectDuplicateNotice';
import { useProspectDuplicateCheck } from './useProspectDuplicateCheck';
interface NewProspectPageProps {
  profile: AdminProfile;
}
interface DraftPayload {
  version: 3;
  form: ProspectFormValues;
}
type ConfirmationAction = 'cancel' | 'discard' | null;
const DRAFT_KEY = 'titan_os_new_prospect_draft';
export function NewProspectPage({
  profile
}: NewProspectPageProps) {
  const initialDraftRef = useRef<DraftPayload | null | undefined>(undefined);
  if (initialDraftRef.current === undefined) {
    initialDraftRef.current = readDraft();
  }
  const [form, setForm] = useState<ProspectFormValues>(() => initialDraftRef.current?.form ?? createInitialForm());
  const [niches, setNiches] = useState<CrmRecord[]>([]);
  const [sources, setSources] = useState<CrmRecord[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [restoredDraft, setRestoredDraft] = useState(Boolean(initialDraftRef.current));
  const [hasDraft, setHasDraft] = useState(Boolean(initialDraftRef.current));
  const [dirty, setDirty] = useState(Boolean(initialDraftRef.current));
  const [confirmationAction, setConfirmationAction] = useState<ConfirmationAction>(null);
  const formRef = useRef(form);
  const dirtyRef = useRef(dirty);
  formRef.current = form;
  dirtyRef.current = dirty;
  const identity = useMemo<ProspectIdentityValues>(() => ({
    businessName: form.business_name,
    phone: form.phone,
    whatsapp: form.whatsapp,
    email: form.email,
    instagram: form.instagram_url,
    facebook: form.facebook_url,
    website: form.website_url,
    googleMaps: form.google_maps_url
  }), [form.business_name, form.phone, form.whatsapp, form.email, form.instagram_url, form.facebook_url, form.website_url, form.google_maps_url]);
  const duplicateState = useProspectDuplicateCheck(identity);
  const persistDraft = useCallback(() => {
    if (!dirtyRef.current) return;
    try {
      const draft: DraftPayload = {
        version: 3,
        form: formRef.current
      };
      window.localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    } catch {
      // Local recovery must never interrupt entry or submit.
    }
  }, []);
  useEffect(() => {
    void loadOptions();
  }, []);
  useEffect(() => {
    if (!dirty) return undefined;
    const timer = window.setTimeout(() => {
      persistDraft();
      setHasDraft(true);
    }, 400);
    return () => window.clearTimeout(timer);
  }, [dirty, form, persistDraft]);
  useEffect(() => {
    return () => persistDraft();
  }, [persistDraft]);
  useEffect(() => {
    const saveImmediately = () => persistDraft();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') saveImmediately();
    };
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!dirtyRef.current) return;
      saveImmediately();
      event.preventDefault();
      event.returnValue = '';
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', saveImmediately);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', saveImmediately);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [persistDraft]);
  async function loadOptions() {
    setLoadingOptions(true);
    setOptionsError(null);
    try {
      const options = await ProspectService.listFormOptions();
      setNiches(options.niches as CrmRecord[]);
      setSources(options.sources as CrmRecord[]);
    } catch {
      setOptionsError('Algunas listas no están disponibles. Podés guardar el prospecto de todas formas.');
    } finally {
      setLoadingOptions(false);
    }
  }
  function updateField(field: keyof ProspectFormValues, fieldValue: string) {
    setForm(current => ({
      ...current,
      [field]: fieldValue
    }));
    setDirty(true);
    setHasDraft(true);
    setRestoredDraft(false);
    setError(null);
  }
  function requestCancel() {
    if (!dirtyRef.current) {
      navigate('/admin/prospectos');
      return;
    }
    setConfirmationAction('cancel');
  }
  function requestDiscard() {
    setConfirmationAction('discard');
  }
  function confirmAction() {
    if (confirmationAction === 'discard') {
      clearDraft();
      dirtyRef.current = false;
      setForm(createInitialForm());
      setDirty(false);
      setHasDraft(false);
      setRestoredDraft(false);
      setError(null);
    }
    if (confirmationAction === 'cancel') {
      persistDraft();
      dirtyRef.current = false;
      setDirty(false);
      navigate('/admin/prospectos');
    }
    setConfirmationAction(null);
  }
  function clearDraft() {
    try {
      window.localStorage.removeItem(DRAFT_KEY);
    } catch {
      // Storage access is not required for creating a prospect.
    }
  }
  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const prospect = await ProspectService.create(form, profile.id);
      const id = value(prospect as CrmRecord, 'id', 'prospect_id');
      dirtyRef.current = false;
      clearDraft();
      setDirty(false);
      setHasDraft(false);
      navigate(id ? `/admin/prospectos/${id}` : '/admin/prospectos');
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'No fue posible guardar el prospecto.');
    } finally {
      setSubmitting(false);
    }
  }
  return <>
      <div className="mx-auto max-w-5xl px-4 py-7 sm:px-6 lg:px-8" data-id="element-308">
        <header className="flex flex-col gap-4 border-b border-white/10 pb-6 sm:flex-row sm:items-start sm:justify-between" data-id="element-309">
          <div data-id="element-310">
            <p className="font-orbitron text-xs font-bold tracking-[.18em] text-cyan-200" data-id="element-311">
              CRM COMERCIAL
            </p>
            <h1 className="mt-2 text-3xl font-bold text-white" data-id="element-312">
              Nuevo prospecto
            </h1>
            <p className="mt-2 text-sm text-slate-400" data-id="element-313">
              Solo el nombre del negocio es obligatorio. El resto se puede
              completar después.
            </p>
          </div>

          {hasDraft && <button type="button" onClick={requestDiscard} className="inline-flex min-h-10 shrink-0 items-center justify-center gap-2 rounded-xl border border-white/10 px-3 text-sm font-semibold text-slate-300 hover:bg-white/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300" data-id="element-314">
              <Trash2Icon className="h-4 w-4" data-id="element-315" />
              Descartar borrador
            </button>}
        </header>

        {restoredDraft && <p role="status" className="mt-5 flex items-center gap-2 rounded-xl border border-cyan-300/20 bg-cyan-300/10 p-3 text-sm text-cyan-100" data-id="element-316">
            <FileWarningIcon className="h-4 w-4 shrink-0" data-id="element-317" />
            Restauramos tu borrador sin guardar.
          </p>}

        {optionsError && <p role="status" className="mt-5 rounded-xl border border-amber-300/20 bg-amber-300/10 p-3 text-sm text-amber-100" data-id="element-318">
            {optionsError}
          </p>}

        <form className="mt-6 space-y-6" onSubmit={handleSubmit} data-id="element-319">
          <FormSection title="Información principal" data-id="element-320">
            <Field label="Nombre del negocio" name="business_name" required value={form.business_name} onChange={fieldValue => updateField('business_name', fieldValue)} data-id="element-321" />
            <Field label="Nombre del contacto" name="contact_name" value={form.contact_name} onChange={fieldValue => updateField('contact_name', fieldValue)} data-id="element-322" />
            <Field label="Puesto o rol del contacto" name="contact_role" value={form.contact_role} onChange={fieldValue => updateField('contact_role', fieldValue)} data-id="element-323" />
            <Field label="Teléfono" name="phone" inputMode="tel" value={form.phone} onChange={fieldValue => updateField('phone', fieldValue)} data-id="element-324" />
            <Field label="WhatsApp" name="whatsapp" inputMode="tel" value={form.whatsapp} onChange={fieldValue => updateField('whatsapp', fieldValue)} placeholder="+506 8888-8888" data-id="element-325" />
            <Field label="Correo electrónico" name="email" inputMode="email" value={form.email} onChange={fieldValue => updateField('email', fieldValue)} data-id="element-326" />
            <Field label="Provincia" name="province" value={form.province} onChange={fieldValue => updateField('province', fieldValue)} data-id="element-327" />
            <Field label="Ciudad" name="city" value={form.city} onChange={fieldValue => updateField('city', fieldValue)} data-id="element-328" />
            <Field label="Dirección" name="address" value={form.address} onChange={fieldValue => updateField('address', fieldValue)} data-id="element-329" />
          </FormSection>

          <FormSection title="Origen y asignación" data-id="element-330">
            <SelectField label="Nicho" name="niche_id" value={form.niche_id} onChange={fieldValue => updateField('niche_id', fieldValue)} disabled={loadingOptions} options={niches.map(item => ({
            id: value(item, 'id'),
            label: value(item, 'name', 'label', 'title')
          }))} data-id="element-331" />
            <SelectField label="Fuente" name="source_id" value={form.source_id} onChange={fieldValue => updateField('source_id', fieldValue)} disabled={loadingOptions} options={sources.map(item => ({
            id: value(item, 'id'),
            label: value(item, 'name', 'label', 'title')
          }))} data-id="element-332" />
            <div className="rounded-xl border border-cyan-300/15 bg-cyan-300/[0.06] p-3" data-id="element-333">
              <p className="text-sm font-medium text-slate-200" data-id="element-334">Responsable</p>
              <p className="mt-1.5 flex items-center gap-2 text-sm font-semibold text-cyan-100" data-id="element-335">
                <UserRoundCheckIcon className="h-4 w-4" data-id="element-336" />
                {profile.full_name || profile.email || 'Usuario autenticado'}
              </p>
              <p className="mt-1 text-xs text-slate-500" data-id="element-337">
                Se asigna automáticamente al guardar.
              </p>
            </div>
            <SelectField label="Nivel de interés" name="interest_level" value={form.interest_level} onChange={fieldValue => updateField('interest_level', fieldValue)} options={[{
            id: 'cold',
            label: 'Frío'
          }, {
            id: 'warm',
            label: 'Tibio'
          }, {
            id: 'hot',
            label: 'Caliente'
          }]} data-id="element-338" />
            <Field label="Interés de producto o servicio" name="product_interest" value={form.product_interest} onChange={fieldValue => updateField('product_interest', fieldValue)} data-id="element-339" />
            <Field label="Valor estimado" name="estimated_value" inputMode="decimal" value={form.estimated_value} onChange={fieldValue => updateField('estimated_value', fieldValue)} placeholder="Ej. 150000" data-id="element-340" />
            <SelectField label="Moneda" name="currency" value={form.currency} onChange={fieldValue => updateField('currency', fieldValue)} options={[{
            id: 'CRC',
            label: 'CRC · Colón costarricense'
          }, {
            id: 'USD',
            label: 'USD · Dólar estadounidense'
          }]} data-id="element-341" />
          </FormSection>

          <FormSection title="Identificadores digitales" data-id="element-342">
            <Field label="Sitio web" name="website_url" inputMode="url" value={form.website_url} onChange={fieldValue => updateField('website_url', fieldValue)} placeholder="https://negocio.com" data-id="element-343" />
            <Field label="Instagram" name="instagram_url" inputMode="url" value={form.instagram_url} onChange={fieldValue => updateField('instagram_url', fieldValue)} placeholder="https://instagram.com/negocio" data-id="element-344" />
            <Field label="Facebook" name="facebook_url" inputMode="url" value={form.facebook_url} onChange={fieldValue => updateField('facebook_url', fieldValue)} data-id="element-345" />
            <Field label="Google Maps" name="google_maps_url" inputMode="url" value={form.google_maps_url} onChange={fieldValue => updateField('google_maps_url', fieldValue)} data-id="element-346" />
          </FormSection>

          <ProspectDuplicateNotice state={duplicateState} similarConfirmed={false} onSimilarConfirmed={() => undefined} nonBlocking data-id="element-347" />

          <FormSection title="Investigación y notas" data-id="element-348">
            <TextAreaField label="Notas de investigación" name="research_notes" value={form.research_notes} onChange={fieldValue => updateField('research_notes', fieldValue)} rows={4} data-id="element-349" />
            <TextAreaField label="Notas iniciales" name="notes" value={form.notes} onChange={fieldValue => updateField('notes', fieldValue)} rows={5} data-id="element-350" />
          </FormSection>

          {error && <p role="alert" className="flex gap-2 rounded-xl border border-red-400/20 bg-red-400/10 p-3 text-sm text-red-100" data-id="element-351">
              <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" data-id="element-352" />
              {error}
            </p>}

          <div className="flex flex-col-reverse gap-3 border-t border-white/10 pt-5 sm:flex-row sm:justify-end" data-id="element-353">
            <button type="button" onClick={requestCancel} className="inline-flex min-h-11 items-center justify-center rounded-xl border border-white/10 px-4 text-sm font-semibold text-slate-200 hover:bg-white/5" data-id="element-354">
              Cancelar
            </button>
            <button type="submit" disabled={submitting} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-cyan-300 px-5 text-sm font-bold text-[#06202a] hover:bg-cyan-200 disabled:cursor-not-allowed disabled:opacity-50" data-id="element-355">
              {submitting ? <LoaderCircleIcon className="h-4 w-4 animate-spin" data-id="element-356" /> : <SaveIcon className="h-4 w-4" data-id="element-357" />}
              {submitting ? 'Guardando…' : 'Guardar prospecto'}
            </button>
          </div>
        </form>
      </div>

      {confirmationAction && <ConfirmationDialog action={confirmationAction} onCancel={() => setConfirmationAction(null)} onConfirm={confirmAction} data-id="element-358" />}
    </>;
}
function FormSection({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <section className="rounded-2xl border border-white/10 bg-[#0b1728] p-5 sm:p-6" data-id="element-359">
      <h2 className="text-lg font-bold text-white" data-id="element-360">{title}</h2>
      <div className="mt-5 grid gap-4 md:grid-cols-2" data-id="element-361">{children}</div>
    </section>;
}
function Field({
  label,
  name,
  value,
  onChange,
  required = false,
  placeholder,
  inputMode
}: {
  label: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  placeholder?: string;
  inputMode?: React.InputHTMLAttributes<HTMLInputElement>['inputMode'];
}) {
  return <label data-id="element-362">
      <span className="mb-2 block text-sm font-medium text-slate-200" data-id="element-363">
        {label}
        {required && <span className="ml-1 text-cyan-200" data-id="element-364">*</span>}
      </span>
      <input name={name} required={required} type="text" inputMode={inputMode} value={value} onChange={event => onChange(event.target.value)} placeholder={placeholder} className="min-h-11 w-full select-text rounded-xl border border-white/10 bg-[#07111f] px-3 text-sm text-white outline-none placeholder:text-slate-500 focus:border-cyan-300/60 focus:ring-2 focus:ring-cyan-300/20" data-id="element-365" />
    </label>;
}
function TextAreaField({
  label,
  name,
  value,
  onChange,
  rows
}: {
  label: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  rows: number;
}) {
  return <label className="md:col-span-2" data-id="element-366">
      <span className="mb-2 block text-sm font-medium text-slate-200" data-id="element-367">
        {label}
      </span>
      <textarea name={name} value={value} onChange={event => onChange(event.target.value)} rows={rows} className="w-full select-text resize-y rounded-xl border border-white/10 bg-[#07111f] px-3 py-3 text-sm text-white outline-none focus:border-cyan-300/60 focus:ring-2 focus:ring-cyan-300/20" data-id="element-368" />
    </label>;
}
function SelectField({
  label,
  name,
  value,
  onChange,
  options,
  disabled = false
}: {
  label: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  options: {
    id: string;
    label: string;
  }[];
  disabled?: boolean;
}) {
  return <label data-id="element-369">
      <span className="mb-2 block text-sm font-medium text-slate-200" data-id="element-370">
        {label}
      </span>
      <select name={name} value={value} onChange={event => onChange(event.target.value)} disabled={disabled} className="min-h-11 w-full rounded-xl border border-white/10 bg-[#07111f] px-3 text-sm text-white outline-none focus:border-cyan-300/60 focus:ring-2 focus:ring-cyan-300/20 disabled:opacity-60" data-id="element-371">
        <option value="" data-id="element-372">Sin asignar</option>
        {options.filter(option => option.id && option.label).map(option => <option key={option.id} value={option.id} data-id="element-373">
              {option.label}
            </option>)}
      </select>
    </label>;
}
function ConfirmationDialog({
  action,
  onCancel,
  onConfirm
}: {
  action: Exclude<ConfirmationAction, null>;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const isDiscard = action === 'discard';
  return <div className="fixed inset-0 z-50 flex items-end bg-slate-950/75 sm:items-center sm:justify-center sm:p-6" data-id="element-374">
      <section aria-modal="true" aria-labelledby="draft-confirmation-title" role="dialog" className="w-full rounded-t-3xl border border-white/10 bg-[#0b1728] p-6 shadow-2xl sm:max-w-md sm:rounded-2xl" data-id="element-375">
        <h2 id="draft-confirmation-title" className="text-lg font-bold text-white" data-id="element-376">
          {isDiscard ? '¿Descartar borrador?' : '¿Salir sin guardar?'}
        </h2>
        <p className="mt-2 text-sm leading-6 text-slate-300" data-id="element-377">
          {isDiscard ? 'Se eliminará el borrador local y se limpiarán los datos escritos.' : 'Tu borrador se conservará localmente para que puedas retomarlo después.'}
        </p>
        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end" data-id="element-378">
          <button type="button" onClick={onCancel} className="rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-300 hover:bg-white/5" data-id="element-379">
            Continuar editando
          </button>
          <button type="button" onClick={onConfirm} className={`rounded-xl px-4 py-2.5 text-sm font-bold ${isDiscard ? 'bg-rose-300 text-rose-950 hover:bg-rose-200' : 'bg-cyan-300 text-[#06202a] hover:bg-cyan-200'}`} data-id="element-380">
            {isDiscard ? 'Descartar borrador' : 'Salir y conservar borrador'}
          </button>
        </div>
      </section>
    </div>;
}
function createInitialForm(): ProspectFormValues {
  return createEmptyProspectForm();
}
function readDraft(): DraftPayload | null {
  try {
    const raw = window.localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      form?: Record<string, unknown>;
    };
    if (!parsed.form || typeof parsed.form !== 'object') return null;
    return {
      version: 3,
      form: normalizeDraftForm(parsed.form)
    };
  } catch {
    return null;
  }
}
function normalizeDraftForm(draft: Record<string, unknown>): ProspectFormValues {
  const normalized = createInitialForm();
  for (const key of Object.keys(normalized) as (keyof ProspectFormValues)[]) {
    const draftValue = draft[key];
    if (typeof draftValue === 'string') normalized[key] = draftValue;
  }
  const legacyUrlFields: [keyof ProspectFormValues, string][] = [['instagram_url', 'instagram'], ['facebook_url', 'facebook'], ['website_url', 'website'], ['google_maps_url', 'google_maps']];
  for (const [target, legacyKey] of legacyUrlFields) {
    if (!normalized[target] && typeof draft[legacyKey] === 'string') {
      normalized[target] = draft[legacyKey] as string;
    }
  }
  return normalized;
}
function navigate(path: string) {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}